import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  type Node,
  type Edge,
  Position,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { orpc } from '@/shared/api/client';
import { galactica, galacticaCassiopeia } from '@/shared/config';

type HealthStatus = 'healthy' | 'warning' | 'error' | 'unknown';

interface SystemHealthData {
  webhooks: {
    lastReceived: number | null;
    status: string;
    source: string;
  };
  syncJobs: {
    walletSync: { status: string; lastRun: number | null; processed: number; failed: number };
    vegnetSync: { status: string; lastRun: number | null; processed: number; failed: number };
  };
  blockchain: {
    mainnet: { accessible: boolean; latency: number | null };
    testnet: { accessible: boolean; latency: number | null };
  };
  database: {
    userCount: number;
    actionCount: number;
  };
}

function formatTimeAgo(timestamp: number | null): string {
  if (!timestamp) return 'Never';
  const seconds = Math.floor(Date.now() / 1000) - timestamp;
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function getTimeBasedStatus(timestamp: number | null): HealthStatus {
  if (!timestamp) return 'unknown';
  const seconds = Math.floor(Date.now() / 1000) - timestamp;
  if (seconds < 300) return 'healthy';
  if (seconds < 3600) return 'warning';
  return 'error';
}

function getSyncJobStatus(job: { lastRun: number | null; failed: number }): HealthStatus {
  if (!job.lastRun) return 'unknown';
  if (job.failed > 0) return 'warning';
  return getTimeBasedStatus(job.lastRun);
}

function getRpcStatus(accessible: boolean): HealthStatus {
  return accessible ? 'healthy' : 'error';
}

const statusColors: Record<HealthStatus, { bg: string; border: string; dot: string }> = {
  healthy: { bg: '#ecfdf5', border: '#34d399', dot: '#10b981' },
  warning: { bg: '#fffbeb', border: '#fbbf24', dot: '#f59e0b' },
  error: { bg: '#fef2f2', border: '#f87171', dot: '#ef4444' },
  unknown: { bg: '#f8fafc', border: '#cbd5e1', dot: '#94a3b8' },
};

interface NodeData extends Record<string, unknown> {
  name: string;
  status: HealthStatus;
  detail?: string;
  subtext?: string;
  href?: string;
}

function CustomNode({ data }: { data: NodeData }) {
  const colors = statusColors[data.status];

  const content = (
    <div
      style={{
        background: colors.bg,
        border: `2px solid ${colors.border}`,
        borderRadius: '12px',
        padding: '10px 16px',
        minWidth: '140px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: colors.dot,
            animation: data.status === 'healthy' ? 'pulse 2s infinite' : 'none',
          }}
        />
        <span style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>
          {data.name}
        </span>
      </div>
      {data.detail && (
        <p style={{ fontSize: '12px', color: '#475569', marginTop: '4px', fontFamily: 'monospace' }}>
          {data.detail}
        </p>
      )}
      {data.subtext && (
        <p style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
          {data.subtext}
        </p>
      )}
    </div>
  );

  return (
    <>
      <Handle type="target" position={Position.Top} style={{ visibility: 'hidden' }} />
      <Handle type="target" position={Position.Left} style={{ visibility: 'hidden' }} id="left" />
      {data.href ? (
        <a href={data.href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          {content}
        </a>
      ) : content}
      <Handle type="source" position={Position.Bottom} style={{ visibility: 'hidden' }} />
      <Handle type="source" position={Position.Right} style={{ visibility: 'hidden' }} id="right" />
    </>
  );
}

const nodeTypes = { custom: CustomNode };

export function SystemHealthGraph() {
  const [healthData, setHealthData] = useState<SystemHealthData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    try {
      const data = await orpc.health.system({});
      setHealthData(data);
    } catch (err) {
      console.error('Failed to fetch system health:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const { nodes, edges } = useMemo(() => {
    if (!healthData) {
      return { nodes: [], edges: [] };
    }

    const webhookStatus = getTimeBasedStatus(healthData.webhooks.lastReceived);
    const walletSyncStatus = getSyncJobStatus(healthData.syncJobs.walletSync);
    const vegnetSyncStatus = getSyncJobStatus(healthData.syncJobs.vegnetSync);
    const mainnetStatus = getRpcStatus(healthData.blockchain.mainnet.accessible);
    const testnetStatus = getRpcStatus(healthData.blockchain.testnet.accessible);
    const rewardsStatus: HealthStatus = healthData.database.actionCount > 0 ? 'healthy' : 'unknown';

    const nodes: Node<NodeData>[] = [
      // Row 1: Sources
      {
        id: 'claimr',
        type: 'custom',
        position: { x: 50, y: 0 },
        data: {
          name: 'Claimr',
          status: webhookStatus,
          detail: 'User actions & XP',
          subtext: formatTimeAgo(healthData.webhooks.lastReceived),
          href: 'https://claimr.io',
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      },
      {
        id: 'ubi-board',
        type: 'custom',
        position: { x: 280, y: 0 },
        data: {
          name: 'ubi-board',
          status: webhookStatus,
          detail: 'Webhook proxy',
          subtext: healthData.webhooks.source === 'claimr' ? 'Direct' : 'Proxied',
        },
        sourcePosition: Position.Bottom,
        targetPosition: Position.Left,
      },
      // Row 2: Core
      {
        id: 'gubi-admin',
        type: 'custom',
        position: { x: 165, y: 120 },
        data: {
          name: 'gubi-admin',
          status: 'healthy',
          detail: 'This system',
          subtext: 'Cloudflare Worker',
        },
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
      },
      // Row 3: Jobs
      {
        id: 'wallet-sync',
        type: 'custom',
        position: { x: 0, y: 250 },
        data: {
          name: 'Wallet Sync',
          status: walletSyncStatus,
          detail: healthData.syncJobs.walletSync.failed > 0
            ? `${healthData.syncJobs.walletSync.failed} failed`
            : `${healthData.syncJobs.walletSync.processed} synced`,
          subtext: formatTimeAgo(healthData.syncJobs.walletSync.lastRun),
        },
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
      },
      {
        id: 'vegnet-sync',
        type: 'custom',
        position: { x: 185, y: 250 },
        data: {
          name: 'veGNET Sync',
          status: vegnetSyncStatus,
          detail: healthData.syncJobs.vegnetSync.failed > 0
            ? `${healthData.syncJobs.vegnetSync.failed} failed`
            : `${healthData.syncJobs.vegnetSync.processed} synced`,
          subtext: formatTimeAgo(healthData.syncJobs.vegnetSync.lastRun),
        },
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
      },
      {
        id: 'rewards-calc',
        type: 'custom',
        position: { x: 370, y: 250 },
        data: {
          name: 'Rewards Calc',
          status: rewardsStatus,
          detail: `${healthData.database.actionCount} actions`,
          subtext: 'Every 2 min',
        },
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
      },
      // Row 4: Dependencies
      {
        id: 'claimr-api',
        type: 'custom',
        position: { x: 0, y: 380 },
        data: {
          name: 'Claimr API',
          status: walletSyncStatus === 'error' ? 'warning' : 'healthy',
          detail: 'Wallet lookup',
        },
        targetPosition: Position.Top,
      },
      {
        id: 'mainnet',
        type: 'custom',
        position: { x: 185, y: 380 },
        data: {
          name: 'Mainnet',
          status: mainnetStatus,
          detail: healthData.blockchain.mainnet.latency ? `${healthData.blockchain.mainnet.latency}ms` : 'RPC',
          subtext: `Chain ${galactica.id}`,
          href: galactica.blockExplorers?.default.url,
        },
        targetPosition: Position.Top,
      },
      {
        id: 'testnet',
        type: 'custom',
        position: { x: 370, y: 380 },
        data: {
          name: 'Testnet',
          status: testnetStatus,
          detail: healthData.blockchain.testnet.latency ? `${healthData.blockchain.testnet.latency}ms` : 'RPC',
          subtext: `Chain ${galacticaCassiopeia.id}`,
          href: galacticaCassiopeia.blockExplorers?.default.url,
        },
        targetPosition: Position.Top,
      },
    ];

    const edgeStyle = { stroke: '#94a3b8', strokeWidth: 2 };
    const arrowMarker = { type: MarkerType.ArrowClosed, color: '#94a3b8' };

    const edges: Edge[] = [
      // Claimr → ubi-board (horizontal)
      {
        id: 'claimr-ubi-board',
        source: 'claimr',
        target: 'ubi-board',
        sourceHandle: 'right',
        targetHandle: 'left',
        type: 'default',
        animated: true,
        style: edgeStyle,
        markerEnd: arrowMarker,
      },
      // ubi-board → gubi-admin (vertical down)
      {
        id: 'ubi-board-gubi-admin',
        source: 'ubi-board',
        target: 'gubi-admin',
        type: 'default',
        animated: true,
        style: edgeStyle,
        markerEnd: arrowMarker,
      },
      // gubi-admin → jobs (fan out)
      {
        id: 'gubi-admin-wallet-sync',
        source: 'gubi-admin',
        target: 'wallet-sync',
        type: 'default',
        style: edgeStyle,
        markerEnd: arrowMarker,
      },
      {
        id: 'gubi-admin-vegnet-sync',
        source: 'gubi-admin',
        target: 'vegnet-sync',
        type: 'default',
        style: edgeStyle,
        markerEnd: arrowMarker,
      },
      {
        id: 'gubi-admin-rewards-calc',
        source: 'gubi-admin',
        target: 'rewards-calc',
        type: 'default',
        style: edgeStyle,
        markerEnd: arrowMarker,
      },
      // jobs → dependencies
      {
        id: 'wallet-sync-claimr-api',
        source: 'wallet-sync',
        target: 'claimr-api',
        type: 'default',
        style: edgeStyle,
        markerEnd: arrowMarker,
      },
      // veGNET Sync queries BOTH chains
      {
        id: 'vegnet-sync-mainnet',
        source: 'vegnet-sync',
        target: 'mainnet',
        type: 'default',
        style: edgeStyle,
        markerEnd: arrowMarker,
      },
      {
        id: 'vegnet-sync-testnet',
        source: 'vegnet-sync',
        target: 'testnet',
        type: 'default',
        style: edgeStyle,
        markerEnd: arrowMarker,
      },
      // Rewards Calc is internal (no external deps shown)
    ];

    return { nodes, edges };
  }, [healthData]);

  if (loading) {
    return (
      <div className="h-[500px] flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-400">
          <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin" />
          <span className="text-sm">Loading system health...</span>
        </div>
      </div>
    );
  }

  if (!healthData) {
    return (
      <div className="h-[500px] flex items-center justify-center">
        <p className="text-slate-400 text-sm">Failed to load health data</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 text-xs text-slate-500">
        <span className="font-mono">
          {healthData.database.userCount.toLocaleString()} users • {healthData.database.actionCount.toLocaleString()} actions
        </span>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span>OK</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            <span>Stale</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            <span>Down</span>
          </div>
        </div>
      </div>

      {/* Flow Diagram */}
      <div className="h-[480px] border rounded-lg bg-slate-50">
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
        >
          <Background color="#e2e8f0" gap={16} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  );
}
